import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import {
  AbsoluteLink,
  Alert,
  Button,
  H3,
  ScreenReaderText,
  Stack,
  Text,
} from "atoms";
import { useFollow } from "features/follows";
import { GenericImage, type Image } from "features/images";
import { ReviewableAffiliation } from "features/reviewable";

interface Props {
  id: string;
  name: string;
  badge: Image;
  affiliatedArtists: ReviewableAffiliation[];
}

function CollectiveCard({
  id,
  name,
  badge,
  affiliatedArtists,
  ...props
}: Props) {
  const entityURL = `/collectives/${id}`;
  const artistCount = affiliatedArtists.length;

  const { follow: handleFollowAll, isSuccess } = useFollow({ badge: id });

  return (
    <Card {...props}>
      <CardImageWrapper>
        <CardImage
          src={badge}
          alt={`Featured NFT from ${name} collection`}
          sizes="(max-width: 439px) 100vw, (max-width: 745px) 50vw, (max-width: 1400px) 25%, 310px"
          loading="lazy"
        />
      </CardImageWrapper>

      <AbsoluteLink to={entityURL}>
        <ScreenReaderText>{name}</ScreenReaderText>
      </AbsoluteLink>

      <CardHeader>
        <CardTitle>
          <SH3 size="xs" aria-hidden>
            {name}
          </SH3>
        </CardTitle>

        <Footer>
          <Reviews>
            {artistCount} Artist{artistCount !== 1 ? "s" : ""}
          </Reviews>

          {handleFollowAll && (
            <CollectiveFollowButton
              onClick={handleFollowAll}
              size="xxs"
              variant="secondary"
            >
              Follow All
            </CollectiveFollowButton>
          )}
        </Footer>

        {isSuccess && (
          <FollowSuccess duration={6}>
            <Stack>
              <Text bold size="xs" textAlign="center">
                You followed all {artistCount} artists in {name}
              </Text>
            </Stack>
          </FollowSuccess>
        )}
      </CardHeader>
    </Card>
  );
}

const Card = styled.article(({ theme }) => ({
  background: theme.colors.bgLight,
  borderRadius: theme.borderRadius.sm,
  boxShadow: theme.boxShadow.light,
  overflow: "hidden",
  position: "relative",
  display: "flex",
  flexDirection: "column",
}));

const CardImageWrapper = styled.div({
  alignItems: "center",
  aspectRatio: "1",
  display: "flex",
  flex: "0 0 auto",
  justifyContent: "center",
  margin: 0,
  overflow: "hidden",
  position: "relative",
  "@media (prefers-reduced-motion: no-preference)": {
    transition: "opacity 0.1s ease",
  },
  "&:hover": {
    opacity: 0.7,
  },
});

const CardImage = styled(GenericImage)(({ theme }) => ({
  zIndex: "2",
  "&:before, &>img": {
    objectFit: "cover",
  },
  img: {
    background: theme.colors.bg,
  },
}));

const CardHeader = styled.div({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  gap: 8,
  alignItems: "stretch",
  justifyContent: "space-between",
  padding: "4% 5%",
  position: "relative",
  "&>div:last-of-type": {
    flex: 1,
  },
});

const CardTitle = styled.header(({ theme }) => ({
  flex: 1,
  fontSize: theme.fontSizes.xs,
  paddingTop: ".1em",
  "&:only-child": {
    alignItems: "flex-start",
  },
}));

const SH3 = styled(H3)(({ theme }) => ({
  ...theme.fonts.display,
  alignItems: "center",
  display: "flex",
  lineHeight: "1.1",
  margin: 0,
}));

const Footer = styled.div({
  flex: 1,
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: "1px 0",
  position: "relative",
  justifyContent: "space-between",
});

const Reviews = styled.div(({ theme }) => ({
  color: theme.colors.fg70,
  flex: 1,
  fontSize: 12,
  lineHeight: 1.3,
  marginRight: 10,
  whiteSpace: "nowrap",
}));

const CollectiveFollowButton = styled(Button)({
  fontSize: 12,
  padding: "4px 10px",
  position: "relative",
  zIndex: 11,
});

const messageInOut = keyframes({
  "0%, 100%": {
    opacity: 0,
  },
  "7%, 93%": {
    opacity: 1,
    pointerEvents: "auto",
  },
});

const FollowSuccess = styled(Alert)<{ duration: number }>(({ duration }) => ({
  position: "absolute",
  display: "flex",
  alignItems: "center",
  left: 10,
  right: 10,
  bottom: 10,
  animation: `${duration}s ease 0s forwards ${messageInOut}`,
  pointerEvents: "none",
  minHeight: "calc(100% - 20px)",
}));

FollowSuccess.defaultProps = {
  type: "success",
};

export { CollectiveCard };
